import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import { html, type PropertyValues } from "lit";
import { live } from "lit/directives/live.js";
import { z } from "zod";
import { checkNotNil } from "../check-not-nil";
import { FormAssociationMixin } from "../mixins/form-associated-element";
import { attributeProperty } from "../property.js";
import { DatetimeLocalString, DatetimeString } from "../schemas/datetime";
import type { ParserType } from "../schemas/parser";
import { hostStyles } from "../styles/host.styles";
import { WmElement } from "./element";

type Value = ParserType<typeof Value>;
const Value = attributeProperty("value", DatetimeString.schema);

type Label = ParserType<typeof Label>;
const Label = attributeProperty("label", z.string());

export class WmDatetimeInput extends FormAssociationMixin(WmElement) {
  static readonly TAG_NAME = "wm-datetime-input";

  static override properties = {
    ...super.properties,
    value: Value,
    label: Label,
  };

  static styles = [hostStyles];

  declare readonly label: Label;
  declare value: Value;

  constructor() {
    super();
    this.label = undefined;
    this.value = undefined;
  }

  protected override render() {
    // TODO: double check the DatetimeLocalString codec bullshit here, I think
    // it adds decimals inadvertently, since it includes seconds and micros.
    // TODO: It wouldn't be a bad idea neither to put a min and a max that
    // guarantees that the serialized string fits the expected format (4 chars
    // for years, for example)
    return html`
      <wa-input
        type="datetime-local"
        name=${this.name}
        label=${this.label}
        .value=${live(this.value ? DatetimeLocalString.codec.decode(this.value) : null)}
        @keyup=${this.#handleKeyUp}
        @input=${this.#handleInput}></wa-input>
    `;
  }

  protected override updated(props: PropertyValues<this>): void {
    if (props.has("value")) {
      this.#updateForm();
    }
  }

  // NOTE: This is required here because the input event *only fires on full
  // dates*. This seems to be a choice made by the native implementation of
  // the Chromium browser familiy. The result of that can be confusing:
  // - The user inputs a partial date (without the time, for example)
  // - The user moves on to another field
  // - The user submits
  // - The partial date is simply *omitted*
  //
  // Therefore, we update the validity of the field by watching the keyup
  // event.
  #handleKeyUp(e: KeyboardEvent) {
    const input = checkNotNil(e.target) as WaInput;
    // No need to set the form value on incomplete input. Whatever existed
    // before is fine.
    input.checkValidity();
    this.internals.setValidity(
      input.validity,
      input.validationMessage,
      input.validationTarget,
    );
  }

  #handleInput(_: InputEvent) {
    const input = this.#queryInput();
    if (input.value) {
      this.value = DatetimeLocalString.codec.encode(input.value);
    } else {
      this.value = undefined;
    }
  }

  #updateForm() {
    this.internals.setFormValue(this.value ?? null);
    // Not required at this time, and also no additional constraints yet.
    this.internals.setValidity({});
  }

  #queryInput(): WaInput {
    return this.queryNotNil("wa-input");
  }
}

customElements.define(WmDatetimeInput.TAG_NAME, WmDatetimeInput);

declare global {
  interface HTMLElementTagNameMap {
    [WmDatetimeInput.TAG_NAME]: WmDatetimeInput;
  }
}
